import { jsx as _jsx } from "react/jsx-runtime";
import { Typography, useTheme } from "@mui/material";
import { useCompanyConfigQuery } from "../../SWR/Company/useCompanyConfigQuery";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { recursiveTokenFilter } from "../../Shared/utils/tokenReplacement";
import { useRecipes } from "../OrderFlowProvider/OrderFlow.requests";
import { useOrderFlow } from "../OrderFlowProvider/OrderFlowContext";
import { ModuleTypes, PaperSettings } from "../OrderFlowProvider/OrderFlowTypes";
import { Banner } from "../components/modules/Banner/Banner";
import { DayFilter } from "../components/modules/DayFilter/DayFilter";
import { DayQuantity } from "../components/modules/DayQuantity/DayQuantity";
import { DaysOfWeek } from "../components/modules/DaysOfWeek/DaysOfWeek";
import { Debug } from "../components/modules/DebugModule/Debug";
import { Html } from "../components/modules/Html/Html";
import { MarkdownModule } from "../components/modules/Markdown/MarkdownModule";
import { MealBundleChooser } from "../components/modules/MealBundleChooser/MealBundleChooser";
import { MealQuantity } from "../components/modules/MealQuantity/MealQuantity";
import { MealSize } from "../components/modules/MealSize/MealSize";
import { Omissions } from "../components/modules/Omissions/Omissions";
import { Product } from "../components/modules/Product/components/Product";
import { Questions } from "../components/modules/Questions/Questions";
import { Recipe } from "../components/modules/Recipe/components/Recipe";
import { RecipeFilterModule } from "../components/modules/RecipeFilterModule/RecipeFilterModule";
import { RecipeType } from "../components/modules/RecipeType/RecipeType";
import { StartDate } from "../components/modules/StartDate/StartDate";
import { Subscription } from "../components/modules/Subscription/Subscription";
import { WeekQuantity } from "../components/modules/WeekQuantity/WeekQuantity";
/**
 * Defines helper functions for Order Flow Modules
 */
export const useFlowModule = () => {
    const { recipeModuleDisplayRequirementCheck } = useRecipes();
    const { store } = useOrderFlow();
    const { datadogLog } = useLogging();
    const { tokenMap } = useCompanyConfigQuery();
    const validateModuleConfig = (moduleConfig, availableConfig, moduleType) => {
        if (!moduleConfig || !availableConfig) {
            return false;
        }
        const onFail = () => {
            console.error("Invalid property found in ModuleConfig.");
            return false;
        };
        // if there is a moduleConfig provided that is not listed in availableConfig,
        // this is an invalid configuration.
        let configKeys = availableConfig.map((config) => config.Name);
        recursiveTokenFilter(moduleConfig, false, tokenMap);
        Object.keys(moduleConfig).forEach((key) => {
            if (!configKeys.includes(key)) {
                datadogLog(`Invalid property ${key} found in ModuleConfig for ModuleType ${ModuleTypes[moduleType]}`, LogLevel.WARN);
            }
        });
        // if there is a moduleConfig whose type does not match that listed in availableConfig,
        // this is an invalid configuration.
        let isValid = true;
        availableConfig.forEach((availableConfigKey) => {
            let configTypeValue = availableConfigKey.JsType;
            if (moduleConfig[availableConfigKey.Name] != undefined &&
                typeof moduleConfig[availableConfigKey.Name] !== configTypeValue) {
                console.error(`Invalid Type found in ModuleConfig. 
              Config value: ${moduleConfig[availableConfigKey.Name]}.
              Type of value: ${typeof moduleConfig[availableConfigKey.Name]}.
              Type expected: ${configTypeValue}`);
                isValid = false;
            }
        });
        return isValid;
    };
    const RenderModule = (flowModule, margin) => {
        const moduleType = flowModule.Module.ModuleType;
        const moduleConfig = flowModule.ModuleConfig;
        const availableConfig = flowModule.Module.AvailableConfig;
        const isValid = validateModuleConfig(moduleConfig, availableConfig, moduleType);
        const componentSequenceNumber = flowModule.Sequence;
        if (!isValid) {
            return _jsx(Typography, Object.assign({ color: "error" }, { children: "Invalid module configuration" }));
        }
        switch (moduleType) {
            case ModuleTypes.RECIPE_FILTER:
                return _jsx(RecipeFilterModule, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.START_DATE:
                return _jsx(StartDate, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.DAYS_OF_WEEK:
                return _jsx(DaysOfWeek, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.MEAL_QUANTITY:
                return _jsx(MealQuantity, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.OMISSION:
                return _jsx(Omissions, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.MEAL_SIZE:
                return _jsx(MealSize, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.RECIPE_TYPE:
                return _jsx(RecipeType, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.RECIPE:
                const recipeModuleDisplayReq = recipeModuleDisplayRequirementCheck(moduleConfig.RecipeModuleDisplayRequirement);
                return recipeModuleDisplayReq === undefined ? (_jsx(Recipe, { sequenceNumber: componentSequenceNumber }, flowModule.Id)) : (_jsx(Typography, Object.assign({ color: "error" }, { children: recipeModuleDisplayReq === null || recipeModuleDisplayReq === void 0 ? void 0 : recipeModuleDisplayReq.errorMessage }), flowModule.Id));
            case ModuleTypes.DAY_FILTER:
                return _jsx(DayFilter, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.SUBSCRIPTION:
                return _jsx(Subscription, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.DAY_QUANTITY:
                return _jsx(DayQuantity, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.PRODUCT:
                return _jsx(Product, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.HTML:
                return _jsx(Html, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.DEBUG:
                return _jsx(Debug, {}, flowModule.Id);
            case ModuleTypes.MEAL_BUNDLE_CHOOSER:
                return _jsx(MealBundleChooser, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.QUESTIONS:
                return _jsx(Questions, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.MARKDOWN:
                return _jsx(MarkdownModule, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
            case ModuleTypes.BANNER:
                return _jsx(Banner, { sequenceNumber: componentSequenceNumber, margin: margin }, flowModule.Id);
            case ModuleTypes.WEEK_QUANTITY:
                return _jsx(WeekQuantity, { sequenceNumber: componentSequenceNumber }, flowModule.Id);
        }
    };
    //Sort modules in sequence order for current page
    const sortedModules = store.model.Flow.FlowModules.filter((flowModule) => flowModule.Page === store.cart.currentPage).sort((a, b) => a.Sequence - b.Sequence);
    //Maps which and how many modules live on a single paper component
    let flowModulePaperMap = [];
    sortedModules.map((module, index) => {
        module.ModulesOnSamePaper = [];
        if ((module.ModuleConfig.PaperSetting === PaperSettings.StartNewPaper ||
            module.ModuleConfig.PaperSetting === PaperSettings.ContinuePaper) &&
            !module.isAlreadyOnPaper) {
            for (let i = index + 1; i < sortedModules.length; i++) {
                //If paper does not continue break out and start again
                if (sortedModules[i].ModuleConfig.PaperSetting === PaperSettings.StartNewPaper ||
                    sortedModules[i].ModuleConfig.PaperSetting === PaperSettings.NoPaper)
                    break;
                if (sortedModules[i].ModuleConfig.PaperSetting === PaperSettings.ContinuePaper) {
                    module.ModulesOnSamePaper.push(sortedModules[i]);
                    sortedModules[i].isAlreadyOnPaper = true;
                }
            }
            flowModulePaperMap.push(module);
        }
        if (module.ModuleConfig.PaperSetting === PaperSettings.NoPaper) {
            flowModulePaperMap.push(module);
        }
        //Handles the case where modules have no configurations
        if (module.ModuleConfig.PaperSetting === undefined) {
            flowModulePaperMap.push(module);
        }
    });
    const theme = useTheme();
    const paddingValue = 2;
    const paperPadding = theme.spacing(paddingValue);
    const getRemovePaperPadding = (module) => {
        if (module.ModuleName === "Banner")
            return `-${paperPadding}`;
        else
            return "";
    };
    return {
        RenderModule,
        flowModulePaperMap,
        orderFlowModules: sortedModules,
        paperPadding,
        getRemovePaperPadding,
    };
};
